import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Icon, PopoverMenu, Text } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import {
  ProjectManagementDefaultRateCostDocument,
  ProjectManagementDefaultRateCostQuery,
  ProjectManagementDefaultRateCostQueryVariables,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import RateConfigurationCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard'
import SkeletonLoaderCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard/SkeletonLoaderCard'
import RolesAndLevelsEntitySelector from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsSelector'
import { SelectedLevelItem } from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'
import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { getLevelsDisplayText } from 'modules/project_management/lib/formatText'
import { ProjectManagementRateCreateInput } from 'modules/project_management/pages/ProjectDetailsPage/Employees/RatesSettingsSidepanel/types'

interface JobBasedRatesProps {
  rates: ProjectManagementRateCreateInput[]
  onChange: (rates: ProjectManagementRateCreateInput[]) => void
  isJobCatalogTreeEnabled: boolean
}

const JobBasedRates = ({ rates, onChange, isJobCatalogTreeEnabled }: JobBasedRatesProps) => {
  const [cachedRateCosts, setCachedRateCosts] = useState<Record<number, number>>({})
  const requestedLevelIds = useRef<Set<number>>(new Set())
  const initializedRates = useRef<Set<number>>(new Set())

  const selectedLevels = useMemo(
    () =>
      rates.map((rate) => ({
        id: rate.resourceId,
        name: rate.name || '',
        type: 'level' as const,
        totalCount: 0,
        salaryRanges: [],
        roleName: rate.roleName,
      })),
    [rates]
  )

  const levelIds = useMemo(() => rates.map((rate) => rate.resourceId), [rates])

  const uncachedLevelIds = useMemo(
    () => levelIds.filter((id) => !(id in cachedRateCosts) && !requestedLevelIds.current.has(id)),
    [levelIds, cachedRateCosts]
  )

  const [fetchDefaultRateCosts, { data: defaultRateCostsData }] = useLazyQuery<
    ProjectManagementDefaultRateCostQuery,
    ProjectManagementDefaultRateCostQueryVariables
  >(ProjectManagementDefaultRateCostDocument)

  useUnsafeEffect(() => {
    if (uncachedLevelIds.length > 0) {
      uncachedLevelIds.forEach((id) => requestedLevelIds.current.add(id))
      fetchDefaultRateCosts({
        variables: {
          resourceIds: uncachedLevelIds,
          resourceType: isJobCatalogTreeEnabled ? 'JobCatalog::TreeLevel' : 'JobCatalog::Level',
        },
      })
    }
  }, [uncachedLevelIds, fetchDefaultRateCosts])

  useUnsafeEffect(() => {
    if (defaultRateCostsData?.projectManagement?.defaultRateCostsConnection?.nodes) {
      const newCacheEntries: Record<number, number> = {}
      defaultRateCostsData.projectManagement.defaultRateCostsConnection.nodes.forEach(
        (node: { resourceId: number; chargingCostCents: number }) => {
          newCacheEntries[node.resourceId] = node.chargingCostCents
        }
      )
      setCachedRateCosts((prev) => ({ ...prev, ...newCacheEntries }))
    }
  }, [defaultRateCostsData])

  useEffect(() => {
    // Clean up initialized rates that are no longer in the rates array
    const currentResourceIds = new Set(rates.map((rate) => rate.resourceId))
    initializedRates.current.forEach((resourceId) => {
      if (!currentResourceIds.has(resourceId)) {
        initializedRates.current.delete(resourceId)
      }
    })

    const updatedRates: ProjectManagementRateCreateInput[] = rates.map((rate) => {
      if (rate.id) {
        return rate
      }

      const chargingCostCents = cachedRateCosts[rate.resourceId] ?? rate.chargingCostCents ?? 0
      const isInitialized = initializedRates.current.has(rate.resourceId)

      // Only apply defaults if this rate hasn't been initialized yet and we have default data available
      if (
        !isInitialized &&
        (cachedRateCosts[rate.resourceId] !== undefined || defaultRateCostsData)
      ) {
        const defaultBillingRateCents =
          defaultRateCostsData?.projectManagement?.ratesConnection?.nodes?.find(
            (node) => node.resourceId === rate.resourceId
          )?.billingRateCents ?? chargingCostCents
        const defaultProfitabilityPercentageCents =
          defaultRateCostsData?.projectManagement?.ratesConnection?.nodes?.find(
            (node) => node.resourceId === rate.resourceId
          )?.profitabilityPercentageCents ?? 0

        initializedRates.current.add(rate.resourceId)

        return {
          ...rate,
          chargingCostCents,
          billingRateCents: defaultBillingRateCents,
          profitabilityPercentageCents: defaultProfitabilityPercentageCents,
        }
      }

      // For already initialized rates, only update chargingCostCents if it changed
      return {
        ...rate,
        chargingCostCents,
      }
    })

    const hasChanges = updatedRates.some(
      (rate, idx) =>
        rate.chargingCostCents !== rates[idx]?.chargingCostCents ||
        rate.billingRateCents !== rates[idx]?.billingRateCents ||
        rate.profitabilityPercentageCents !== rates[idx]?.profitabilityPercentageCents
    )

    if (hasChanges) {
      onChange(updatedRates)
    }
  }, [cachedRateCosts, rates, defaultRateCostsData, onChange])

  const handleLevelsChange = (values: { levels?: SelectedLevelItem[] }) => {
    const newLevels = values.levels || []
    const newLevelIds = newLevels.map((l) => l.id)

    const filteredRates = rates.filter((rate) => newLevelIds.includes(rate.resourceId))

    const newRates = [...filteredRates]
    newLevels.forEach((level) => {
      if (!newRates.some((rate) => rate.resourceId === level.id)) {
        const costCents = cachedRateCosts[level.id] ?? 0
        const billingRateCents =
          defaultRateCostsData?.projectManagement?.ratesConnection?.nodes?.find(
            (node) => node.resourceId === level.id
          )?.billingRateCents ?? costCents
        const profitabilityPercentageCents =
          defaultRateCostsData?.projectManagement?.ratesConnection?.nodes?.find(
            (node) => node.resourceId === level.id
          )?.profitabilityPercentageCents ?? 0

        newRates.push({
          resourceId: level.id,
          resourceType: isJobCatalogTreeEnabled ? 'JobCatalog::TreeLevel' : 'JobCatalog::Level',
          billingRateCents: billingRateCents,
          chargingCostCents: costCents,
          profitabilityPercentageCents: profitabilityPercentageCents,
          currency: DEFAULT_CURRENCY,
          name: level.name,
          roleName: level.roleName,
        })
      }
    })

    onChange(newRates)
  }

  return (
    <Box gap="s16">
      <PopoverMenu type="dialog">
        <PopoverMenu.Trigger>
          <LabeledInput
            isLegend
            label={i18n.t('project_management.jobs_and_rates.sidepanels.form.select_jobs.label')}
          >
            <Box
              gap="s8"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              border={{
                all: {
                  width: 's2',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
              borderRadius={{
                all: 'abs008',
              }}
              padding="s8"
              paddingX="s16"
              cursor="pointer"
            >
              <Text.Normal ellipsis>{getLevelsDisplayText(selectedLevels.length)}</Text.Normal>
              <Icon.Small icon="caret-down" color="grey600" />
            </Box>
          </LabeledInput>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="center" width="s400" hideWhenDetached>
          <Box alignItems="center">
            <RolesAndLevelsEntitySelector
              selectedLevels={selectedLevels}
              setValues={handleLevelsChange}
              existingResourceJobLevelIds={[]}
              isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
            />
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
      {rates.length > 0 && (
        <Box paddingY="s24">
          <Alert variant="warning">
            <AlertTitle>
              {i18n.t('project_management.forms.billing_rate.alerts.changes_effective.title')}
            </AlertTitle>
            <AlertDescription>
              {i18n.t('project_management.forms.billing_rate.alerts.changes_effective.description')}
            </AlertDescription>
          </Alert>
        </Box>
      )}
      {rates.map((rate) => {
        const hasCostData = cachedRateCosts[rate.resourceId] !== undefined

        if (!hasCostData) {
          return <SkeletonLoaderCard key={rate.resourceId} />
        }

        return (
          <RateConfigurationCard
            key={rate.resourceId}
            currency={rate.currency}
            maxCostRate={rate.chargingCostCents / 100}
            onChangeValue={(rateValues: { billRate: string; minProfitMargin: string }) => {
              const billRateCents = Math.round(parseFloat(rateValues.billRate) * 100)
              const profitabilityPercentageCents = Math.round(
                parseFloat(rateValues.minProfitMargin) * 100
              )

              const newRates = rates.map((existingRate) =>
                existingRate.resourceId === rate.resourceId
                  ? {
                      ...existingRate,
                      billingRateCents: billRateCents,
                      profitabilityPercentageCents: profitabilityPercentageCents,
                    }
                  : existingRate
              )

              onChange(newRates)
            }}
            onDelete={() => {
              onChange(rates.filter((r) => r.resourceId !== rate.resourceId))
            }}
            title={rate.roleName ? `${rate.roleName} ${rate.name}` : rate.name || ''}
            disabledAllFields={false}
            profitMargin={rate.profitabilityPercentageCents / 100}
            initialBillRate={(rate.billingRateCents / 100).toFixed(2)}
          />
        )
      })}
    </Box>
  )
}

export default JobBasedRates
